import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";

export type CommandCategory = "navigation" | "action" | "search";

export interface Command {
  readonly id: string;
  readonly label: string;
  readonly icon?: ComponentType<{ size?: number }>;
  readonly category?: CommandCategory;
  readonly shortcut?: string;
  readonly action: () => void | Promise<void>;
  readonly requires?: { action: string; resource: string };
}

interface CommandRegistryContextValue {
  readonly commands: ReadonlyArray<Command>;
  readonly register: (cmd: Command) => () => void;
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
}

const CommandRegistryContext = createContext<CommandRegistryContextValue | null>(null);

export function CommandRegistryProvider({ children }: { children: ReactNode }) {
  const [commands, setCommands] = useState<ReadonlyArray<Command>>([]);
  const [open, setOpen] = useState(false);

  const register = useCallback((cmd: Command) => {
    setCommands((prev) => {
      if (prev.some((c) => c.id === cmd.id)) {
        return prev.map((c) => (c.id === cmd.id ? cmd : c));
      }
      return [...prev, cmd];
    });
    return () => {
      setCommands((prev) => prev.filter((c) => c.id !== cmd.id));
    };
  }, []);

  const value = useMemo(() => ({ commands, register, open, setOpen }), [commands, register, open]);

  return (
    <CommandRegistryContext.Provider value={value}>{children}</CommandRegistryContext.Provider>
  );
}

export function useCommandRegistry(): CommandRegistryContextValue {
  const ctx = useContext(CommandRegistryContext);
  if (!ctx) throw new Error("useCommandRegistry used outside provider");
  return ctx;
}

/**
 * Registers a command for the lifetime of the calling component.
 * Cleanup on unmount.
 */
export function useRegisterCommand(cmd: Command, deps: ReadonlyArray<unknown> = []): void {
  const { register } = useCommandRegistry();
  useEffect(() => {
    const unregister = register(cmd);
    return unregister;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
